import React, { useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
// mui components
import { Typography, Grid, Avatar } from '@mui/material';
// components
import { ActivityButtonPostItem } from './components/activity-button-post-item';
// custom hooks
import { useSidePanel } from 'hooks/useSidePanel';
// jotai
import { useAtomValue, useSetAtom } from 'jotai';
import { activityFeedPostsAtom, lastViewedActivityFeedAtom, getUnviewedActivityFeedCount, markActivityFeedViewedAtom } from 'atoms';
// api
import { useGetMeQuery } from 'redux-store/api/auth';
// utils
import { getTimeSpend } from 'utils/helpers';
import { getImageBySize } from 'utils/helpers';
// icons
import NotificationsMessage from 'assets/icons/Comment_new_icon.svg';
// styles
import { styles } from './styles';
import { Badge } from '../components/badge';

interface ActivityButtonProps {
  projectId: number;
  handleSeeMore?: () => void;
}

export const ActivityButton = ({
  projectId,
  handleSeeMore,
}: ActivityButtonProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const activityButtonId = open ? 'activity-button-popover' : undefined;

  const { data: userMe } = useGetMeQuery();
  const allPosts = useAtomValue(activityFeedPostsAtom) || [];
  const lastViewedMap = useAtomValue(lastViewedActivityFeedAtom);
  const markActivityFeedViewed = useSetAtom(markActivityFeedViewedAtom);

  const recentPosts = useMemo(
    () => allPosts.filter((post) => post.createdBy?.userId !== String(userMe?.id)).slice(0, 10),
    [allPosts, userMe?.id]
  );

  const hasData = recentPosts.length > 0;
  const isLoading = false; // Firebase loads synchronously from cache

  const { handleOpenActivityFeed, setProjectId: setProjectIdCtx } = useSidePanel();

  const unviewedCount = useMemo(
    () => getUnviewedActivityFeedCount(allPosts, projectId, userMe?.id || '', lastViewedMap),
    [allPosts, projectId, userMe?.id, lastViewedMap]
  );

  useEffect(() => {
    if (open) {
      setProjectIdCtx(projectId);
    }
  }, [projectId, open]);

  const onOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onClose = () => {
    if (unviewedCount > 0 && userMe?.id) {
      markActivityFeedViewed(userMe.id);
    }
    setAnchorEl(null);
  };

  const onSeeMore = () => {
    onClose();
    handleSeeMore ? handleSeeMore() : handleOpenActivityFeed();
  };

  const handleOpenPostModal = (postId: string) => {
    searchParams.set('postId', postId);
    searchParams.set('projectId', projectId.toString());
    setSearchParams(searchParams);
  };

  const isNoContentVisible = !hasData && open && !isLoading;
  const isLoadingVisible = !hasData && open && isLoading;
  const isContentVisible = hasData && open;

  return (
    <Badge
      onOpen={onOpen}
      onClose={onClose}
      open={open}
      id={activityButtonId}
      popoverProps={{ anchorEl }}
      buttonProps={{ title: `${unviewedCount}`, icon: <NotificationsMessage /> }}
    >
      <Badge.Header heading="Recent Activity" actionText="See more" onActionClick={onSeeMore} />
      <Badge.Container additionalSx={styles.badgeContainer}>
        {isNoContentVisible && (
          <Badge.NoContent heading="No Activity" subheading="Looks like you have no activity yet" />
        )}
        {isLoadingVisible && <Badge.Loading />}
        {isContentVisible && (
          <Badge.InfiniteScroll
            dataLength={recentPosts.length}
            hasMore={false}
            getMore={() => {}}
          >
            {recentPosts.map((post) => {
              const avatar = post.createdBy?.avatarUrl;
              const displayName = post.createdBy?.displayName || '';
              const initials = displayName
                .split(' ')
                .map((n) => n[0])
                .join('')
                .slice(0, 2)
                .toUpperCase();

              return (
                <Grid key={post.id} sx={styles.paperItem}>
                  <Grid sx={styles.contentContainer}>
                    <Grid sx={styles.postContainer}>
                      <Grid sx={styles.customAvatarContainer}>
                        {avatar ? (
                          <Avatar
                            alt="avatar"
                            sx={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '4px',
                            }}
                            src={getImageBySize(avatar, 24, 24)}
                            variant="square"
                          />
                        ) : (
                          <Grid sx={styles.avatarContainer}>
                            <Typography sx={styles.avatar}>{initials}</Typography>
                          </Grid>
                        )}
                      </Grid>

                      <ActivityButtonPostItem
                        post={post}
                        handleOpenPostModal={handleOpenPostModal}
                      />
                    </Grid>
                  </Grid>

                  <Grid sx={styles.dateContainer}>
                    <Typography sx={styles.dateText}>
                      {getTimeSpend(formatDistanceToNowStrict(new Date(post.createdAt)))}
                    </Typography>
                  </Grid>
                </Grid>
              );
            })}
          </Badge.InfiniteScroll>
        )}
      </Badge.Container>
    </Badge>
  );
};
